import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { last, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { AccountSelectors } from '@core/account-data';
import { Analytics } from '@core/analytics-legacy';
import { I18nService } from '@core/i18n-legacy';
import { OfferScope } from '@core/offers-data';
import { ReferralsService } from '@core/referrals-legacy';
import { DialogService, NotificationsController } from '@core/user-interface-legacy';
import { BulkActionsController, BulkActionType } from '@recruitee/bulk-actions-legacy';
import type {
  ActionsVisibilityBreakpoint,
  BulkAction,
  GetBulkActionsProps,
} from '@recruitee/bulk-actions-legacy';

import { OffersBulkActionsExecutorService } from './offers-bulk-actions-executor.service';
import { OffersBulkActionsValidatorService } from './offers-bulk-actions-validator.service';

@Injectable()
export class OffersBulkActionsController extends BulkActionsController {
  protected availableActions: BulkAction[] = [
    {
      id: BulkActionType.Follow,
      buttonLabel: this.i18n.translate('app.bulk_actions.follow'),
      buttonIcon: 'bookmark',
      analytics: '[Offers] Toolbar_BulkActionsFollowButtonClick',
    },
    {
      id: BulkActionType.Unfollow,
      buttonLabel: this.i18n.translate('app.bulk_actions.unfollow'),
      buttonIcon: 'bookmark-border',
      analytics: '[Offers] Toolbar_BulkActionsUnfollowButtonClick',
    },
    {
      id: BulkActionType.ManageEeo,
      buttonLabel: this.i18n.translate('app.bulk_actions.manage_eeo'),
      buttonIcon: 'non-federal-company',
      analytics: '[Offers] Toolbar_BulkActionsManageEEOButtonClick',
    },
    {
      id: BulkActionType.ManageReferrals,
      buttonLabel: this.i18n.translate('app.bulk_actions.manage_referrals'),
      buttonIcon: 'favorite',
      analytics: '[Offers] Toolbar_BulkActionsManageReferralsButtonClick',
    },
    {
      id: BulkActionType.RetrieveArchived,
      buttonLabel: this.i18n.translate('app.bulk_actions.retrieve_archived_jobs'),
      buttonIcon: 'unarchive',
      analytics: '[Offers] Toolbar_BulkActionsRetrieveArchivedButtonClick',
    },
    {
      id: BulkActionType.DeleteArchived,
      buttonLabel: this.i18n.translate('app.bulk_actions.delete'),
      buttonIcon: 'delete',
      analytics: '[Offers] Toolbar_BulkActionsDeleteArchivedButtonClick',
    },
  ];

  private eeoEnabled: boolean;
  private canManageJobsReferralSettings: boolean;
  private canManageJobs: boolean;

  constructor(
    protected notificationsCtrl: NotificationsController,
    protected store: Store,
    protected analytics: Analytics,
    protected dialogService: DialogService,
    protected i18n: I18nService,
    protected referralsService: ReferralsService,
    private executor: OffersBulkActionsExecutorService,
    private validator: OffersBulkActionsValidatorService,
  ) {
    super(notificationsCtrl, store, analytics, dialogService, i18n);

    combineLatest([
      this.store.select(AbilitiesSelectors.abilityMap),
      this.store.select(AccountSelectors.isEEOEnabled),
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([abilitiesMap, eeoEnabled]) => {
        this.canManageJobsReferralSettings = abilitiesMap[Ability.ManageJobsReferralSettings];
        this.canManageJobs = abilitiesMap[Ability.ManageJobs];
        this.eeoEnabled = eeoEnabled;
      });
  }

  public getBulkActions(props: GetBulkActionsProps): BulkAction[] {
    return props?.offersScope === OfferScope.Active
      ? this.getActionsForActiveJobs()
      : this.getActionsForArchivedJobs();
  }

  public dispatchAction(data: { action: BulkAction }): void {
    const { action } = data;
    const offers = this.getSelectedOffers();
    const ids = offers.map(c => c.id);

    if (action.analytics) {
      typeof action.analytics === 'string'
        ? this.analytics.track(action.analytics)
        : action.analytics();
    }

    const actionPermitted = this.validator.isActionPermitted(action.id, ids);
    if (!actionPermitted) return;

    const bulkActionExecution$ = this.executor.getExecutionStream(action, ids);
    bulkActionExecution$.pipe(last()).subscribe({
      next: _ => {
        if (_) {
          // Action may be cancelled before was sent to BE. If we get truthy payload here,
          //  it means action was dispatched
          this.markBulkActionAsCompleted(action);
          this.clearSelection();
        }
      },
      error: error => {
        this.markBulkActionAsCompleted(action);
        this.dispatchActionError(action, error);
      },
    });
  }

  public getActionsVisibilityByBreakpoint(): ActionsVisibilityBreakpoint[] {
    return [
      {
        min: 0,
        max: Number.POSITIVE_INFINITY,
        actionIds: [
          BulkActionType.Follow,
          BulkActionType.Unfollow,
          BulkActionType.RetrieveArchived,
          BulkActionType.DeleteArchived,
        ],
      },
    ];
  }

  protected getSelectedOffers() {
    return this.selectedItems$.getValue();
  }

  private getActionsForActiveJobs(): BulkAction[] {
    const referralsEnabled = this.referralsService.isProgramEnabled();
    return this.availableActions.filter(action => {
      switch (action.id) {
        case BulkActionType.Follow:
        case BulkActionType.Unfollow:
          return true;

        case BulkActionType.ManageEeo:
          return this.eeoEnabled && this.canManageJobs;

        case BulkActionType.ManageReferrals:
          return referralsEnabled && this.canManageJobsReferralSettings;

        default:
          return false;
      }
    });
  }

  private getActionsForArchivedJobs(): BulkAction[] {
    return this.availableActions.filter(action => {
      switch (action.id) {
        case BulkActionType.DeleteArchived:
        case BulkActionType.RetrieveArchived:
          return this.canManageJobs;
        default:
          return false;
      }
    });
  }
}
